import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { FaTimes, FaBars, FaSearch, FaThList, FaSignOutAlt, FaKey } from "react-icons/fa";
import { IconContext } from "react-icons/lib";
import * as actions from "../../actions";
import { logout } from "../../api/Login";
import { GetProductsForHome } from "../../api/Product";
import defaultpic from "../../assets/img/avator/user-default.png";
import {
  Nav,
  NavbarContainer,
  NavLogo,
  HamburgerIcon,
  NavMenu,
  NavItem,
  NavLinks,
  SearchContainer,
  SearchInput,
  SearchButton,
  SearchDropdown,
  ProductName,
  ProductPrice,
  AllResultsButton,
  Avatar,
} from "./Navbar.elements";

const MOBILE_BREAKPOINT = 960;
const SEARCH_DEBOUNCE_MS = 350;
const MAX_SUGGESTIONS = 6;

class Navbar extends React.Component {
  state = {
    isMobileMenuOpen: false,
    showAccountDropdown: false,
    showSearchDropdown: false,
    localQuery: this.props.searchQuery || "",
    searchSuggestions: [],
    suggestionsTotalCount: 0,
    isSearchingSuggestions: false,
  };

  accountDropdownRef = React.createRef();
  searchDropdownRef = React.createRef();
  searchRequestId = 0;
  searchDebounceTimer = null;

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchQuery !== this.props.searchQuery) {
      this.setState({ localQuery: this.props.searchQuery });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    clearTimeout(this.searchDebounceTimer);
  }

  // ----- Mobile menu / account dropdown -----

  handleClickOutside = (event) => {
    if (this.state.isMobileMenuOpen) {
      const nav = document.querySelector("nav");
      if (nav && !nav.contains(event.target)) {
        this.setState({ isMobileMenuOpen: false });
      }
    }

    if (
      this.accountDropdownRef.current &&
      !this.accountDropdownRef.current.contains(event.target)
    ) {
      this.setState({ showAccountDropdown: false });
    }

    if (
      this.searchDropdownRef.current &&
      !this.searchDropdownRef.current.contains(event.target)
    ) {
      this.setState({ showSearchDropdown: false });
    }
  };

  toggleMobileMenu = () => {
    this.setState((prev) => ({ isMobileMenuOpen: !prev.isMobileMenuOpen }));
  };

  closeMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false, showAccountDropdown: false });
  };

  toggleAccountDropdown = () => {
    this.setState((prev) => ({ showAccountDropdown: !prev.showAccountDropdown }));
  };

  // ----- Auth -----

  handleLogout = async () => {
    const res = await logout();
    if (res.status) {
      await this.props.keepUser(res.data);
      this.props.history.push("/home");
    }
  };

  // ----- Search -----

  fetchSearchSuggestions = (query) => {
    clearTimeout(this.searchDebounceTimer);

    const trimmed = query.trim();
    if (!trimmed) {
      this.setState({
        searchSuggestions: [],
        suggestionsTotalCount: 0,
        isSearchingSuggestions: false,
      });
      return;
    }

    this.setState({ isSearchingSuggestions: true });
    const requestId = ++this.searchRequestId;

    this.searchDebounceTimer = setTimeout(async () => {
      try {
        const result = await GetProductsForHome(
          { value: 0 },
          { value: 0 },
          { value: 0 },
          0,
          0,
          "catalog",
          1,
          MAX_SUGGESTIONS,
          trimmed,
        );

        if (requestId !== this.searchRequestId) return; // stale response, ignore

        const records = (result && result.records) || [];
        const suggestions = records.map((row) => ({
          id: row.productId,
          productName: row.productName,
          price: "K " + (row.sellPrice || 0).toLocaleString(),
        }));

        this.setState({
          searchSuggestions: suggestions,
          suggestionsTotalCount: (result && result.totalCount) || suggestions.length,
          isSearchingSuggestions: false,
        });
      } catch (error) {
        if (requestId !== this.searchRequestId) return;
        console.error("Search suggestion error:", error);
        this.setState({
          searchSuggestions: [],
          suggestionsTotalCount: 0,
          isSearchingSuggestions: false,
        });
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  handleSearchChange = (e) => {
    const value = e.target.value;
    this.setState({ localQuery: value, showSearchDropdown: value.length > 0 });

    if (this.props.onSearch) this.props.onSearch(value);
    if (value === "" && this.props.onSearchSubmit) this.props.onSearchSubmit("");

    this.fetchSearchSuggestions(value);
  };

  handleSearchFocus = () => {
    this.setState({ showSearchDropdown: this.state.localQuery.length > 0 });
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.setState({ showSearchDropdown: false });

    if (this.props.onSearchSubmit) {
      this.props.onSearchSubmit(this.state.localQuery);
    }

    this.props.history.push({ pathname: "/home" });
  };

  handleProductSuggestionClick = (product) => {
    this.setState({ localQuery: "", showSearchDropdown: false });
    this.props.history.push(`/home/productDetail/${product.id}`);
    this.closeMobileMenu();
  };

  handleAllResultsClick = () => {
    const { localQuery } = this.state;
    this.setState({ showSearchDropdown: false, localQuery: "" });

    if (this.props.onSearchSubmit) {
      this.props.onSearchSubmit(localQuery);
    }

    this.props.history.push({ pathname: "/home" });
  };

  // ----- Render helpers -----

  renderSearchDropdown() {
    const {
      showSearchDropdown,
      searchSuggestions,
      isSearchingSuggestions,
      suggestionsTotalCount,
      localQuery,
    } = this.state;

    if (!showSearchDropdown) return null;

    const hasMoreResults = suggestionsTotalCount > searchSuggestions.length;

    return (
      <SearchDropdown>
        {isSearchingSuggestions ? (
          <DropdownItem>Searching...</DropdownItem>
        ) : searchSuggestions.length > 0 ? (
          <>
            {searchSuggestions.map((product) => (
              <DropdownItem
                key={product.id}
                onClick={() => this.handleProductSuggestionClick(product)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 15px",
                }}
              >
                <ProductName>{product.productName}</ProductName>
                <ProductPrice>{product.price}</ProductPrice>
              </DropdownItem>
            ))}
            {hasMoreResults && (
              <AllResultsButton onClick={this.handleAllResultsClick}>
                All results
              </AllResultsButton>
            )}
          </>
        ) : (
          <DropdownItem>No products found for "{localQuery}"</DropdownItem>
        )}
      </SearchDropdown>
    );
  }

  renderAccountMenu() {
    const { displayName, profileImage, creditLimit, hasPassword } = this.props.auth || {};

    return (
      <NavItem style={{ position: "relative" }}>
        <Dropdown
          isOpen={this.state.showAccountDropdown}
          toggle={this.toggleAccountDropdown}
          nav
        >
          <DropdownToggle nav caret={false} style={{ padding: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "white",
                cursor: "pointer",
                padding: "10px",
              }}
            >
              <Avatar src={profileImage || defaultpic} alt="Profile" />
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", fontSize: "16px" }}>{displayName}</div>
                <div style={{ fontSize: "0.8rem", color: "#ccc" }}>Points: {creditLimit}</div>
              </div>
            </div>
          </DropdownToggle>
          <DropdownMenu right>
            <DropdownItem header className="text-center">
              <strong>Account</strong>
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                this.props.history.push("/home/list");
                this.closeMobileMenu();
              }}
            >
              <FaThList style={{ marginRight: "8px", color: "#007bff" }} />
              My Orders
            </DropdownItem>
            {/* Show Change Password only for form-registered (non-Google) users */}
            {hasPassword !== false && (
              <DropdownItem
                onClick={() => {
                  this.props.history.push("/user/changepassword");
                  this.closeMobileMenu();
                }}
              >
                <FaKey style={{ marginRight: "8px", color: "#f39c12" }} />
                Change Password
              </DropdownItem>
            )}
            <DropdownItem divider />
            <DropdownItem onClick={this.handleLogout}>
              <FaSignOutAlt style={{ marginRight: "8px", color: "#d00" }} />
              Logout
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </NavItem>
    );
  }

  render() {
    const { isAuth } = this.props.auth || {};

    return (
      <IconContext.Provider value={{ color: "#fff" }}>
        <Nav>
          <NavbarContainer>
            <NavLogo to="/home">
              <img
                src={`${process.env.PUBLIC_URL}/SeaFarerInternetService_1.png`}
                alt="ESM"
              />
            </NavLogo>

            <SearchContainer ref={this.searchDropdownRef}>
              <form onSubmit={this.handleSearchSubmit}>
                <SearchInput
                  type="search"
                  placeholder="Search products..."
                  value={this.state.localQuery}
                  onChange={this.handleSearchChange}
                  onFocus={this.handleSearchFocus}
                />
                <SearchButton type="submit">
                  <FaSearch size={12} />
                </SearchButton>

                {this.renderSearchDropdown()}
              </form>
            </SearchContainer>

            <HamburgerIcon onClick={this.toggleMobileMenu}>
              {this.state.isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            </HamburgerIcon>

            <NavMenu click={this.state.isMobileMenuOpen}>
              {!isAuth ? (
                <>
                  <NavItem>
                    <NavLinks to="/home/data-enquiry">Data Usage</NavLinks>
                  </NavItem>
                  <NavItem>
                    <NavLinks to="/memberLogin" onClick={this.closeMobileMenu}>
                      Login
                    </NavLinks>
                  </NavItem>
                </>
              ) : (
                this.renderAccountMenu()
              )}
            </NavMenu>
          </NavbarContainer>
        </Nav>
      </IconContext.Provider>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, actions)(withRouter(Navbar));